import type { DropdownChangeEvent } from "primereact/dropdown";
import type { Dropdown as PrimeDropdown } from "primereact/dropdown";
import { useEffect, useMemo, useRef, useState } from "react";

import { useStableRest } from "../../../../utils/use-stable-rest";
import { useFilterPanelContext } from "../../filter-panel.context";
import {
  hasFilterCriteria,
  pruneFilterValue,
  serializeFilterValue,
} from "../../filter-panel.model";
import {
  DELETE_SAVED_FILTER_LABEL,
  SAVED_FILTERS_EMPTY_MESSAGE,
  SAVED_FILTERS_PLACEHOLDER,
} from "../../messages";
import type { SavedFilter } from "../../types-filter-panel";
import type { FilterPanelSavedListViewProps, SavedListProps } from "./types-saved-list";

/**
 * ViewModel da SavedList: filtro ativo (vale enquanto o painel espelha os critérios dele) e handlers; sem JSX.
 * Sem escolha explícita, o ativo é o filtro salvo cujos critérios o painel já espelha: assim o recém-salvo aparece
 * selecionado. Excluir não limpa o ativo: ele some quando a tela remove o item de `saved`, depois de confirmar.
 */
export function useFilterPanelSavedList({
  saved,
  onSelect,
  onDelete,
  className,
  ...props
}: SavedListProps): FilterPanelSavedListViewProps {
  const rest = useStableRest(props);
  const panel = useFilterPanelContext();
  const [activeId, setActiveId] = useState<string | null>(null);
  const dropdownRef = useRef<PrimeDropdown>(null);
  const stopRealigningRef = useRef<(() => void) | null>(null);

  const draftKey = serializeFilterValue(pruneFilterValue(panel.draft));
  const hasCriteria = hasFilterCriteria(panel.draft);
  const mirrorsDraft = (item: SavedFilter) => serializeFilterValue(item.value) === draftKey;
  const active =
    saved.find((item) => item.id === activeId && mirrorsDraft(item)) ??
    saved.find((item) => hasCriteria && mirrorsDraft(item)) ??
    null;

  /**
   * Figma 11464:765391/765640: a lista abre alinhada à borda direita do campo (o Prime a alinha à esquerda).
   * Idempotente: mede o painel e o campo na hora.
   */
  const alignPanelRight = () => {
    const panelEl = dropdownRef.current?.getOverlay();
    const field = dropdownRef.current?.getElement();
    if (!panelEl || !field) return;
    const overflow = panelEl.getBoundingClientRect().right - field.getBoundingClientRect().right;
    if (overflow > 0) {
      panelEl.style.left = `${(Number.parseFloat(panelEl.style.left) || 0) - overflow}px`;
    }
  };

  const stopRealigning = () => {
    stopRealigningRef.current?.();
    stopRealigningRef.current = null;
  };

  // O Prime refaz o `left` do painel no resize e na rolagem de ancestrais (`alignOverlay`): o ajuste é refeito logo depois dele.
  const onShow = () => {
    alignPanelRight();
    stopRealigning();
    const realign = () => requestAnimationFrame(alignPanelRight);
    window.addEventListener("resize", realign);
    document.addEventListener("scroll", realign, true);
    stopRealigningRef.current = () => {
      window.removeEventListener("resize", realign);
      document.removeEventListener("scroll", realign, true);
    };
  };

  useEffect(() => () => stopRealigningRef.current?.(), []);

  return useMemo(
    () => ({
      className,
      active: active !== null || undefined,
      rest,
      dropdownRef,
      onShow,
      onHide: stopRealigning,
      testId: panel.partTestId("saved"),
      deleteTestId: panel.partTestId("saved-delete"),
      options: saved.map(({ id, name }) => ({ value: id, label: name })),
      value: active?.id ?? null,
      placeholder: SAVED_FILTERS_PLACEHOLDER,
      emptyMessage: SAVED_FILTERS_EMPTY_MESSAGE,
      empty: saved.length === 0,
      /** Escolher só preenche o painel; a consulta roda em Aplicar. */
      onChange: (event: DropdownChangeEvent) => {
        const chosen = saved.find((item) => item.id === event.value);
        if (!chosen) return;
        setActiveId(chosen.id);
        panel.load(chosen.value);
        onSelect?.(chosen.id);
      },
      delete:
        active && onDelete
          ? {
              label: `${DELETE_SAVED_FILTER_LABEL} ${active.name}`,
              onClick: () => onDelete(active.id),
            }
          : null,
    }),
    // `panel` muda a cada interação; só `load` e `partTestId` são lidos aqui.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [className, saved, active, onSelect, onDelete, rest, panel.partTestId, panel.load],
  );
}
